import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { X, Check, Shirt, Building2, Home, Package, Loader2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import SignatureCanvas from 'react-signature-canvas';
import { toast } from 'sonner';
import { getPrinterInstance } from '@/utils/bluetoothPrinter';

const ModalResumenRecoleccion = ({
  isOpen,
  onClose,
  edificio,
  hospedaje,
  prendas,
  onConfirm
}) => {
  const [receptorNombre, setReceptorNombre] = useState('');
  const [observaciones, setObservaciones] = useState('');
  const [firma, setFirma] = useState(null);
  const [pesoReal, setPesoReal] = useState(''); // Peso real total (kg) confirmado
  const [errors, setErrors] = useState({});
  const [isProcessing, setIsProcessing] = useState(false);
  const sigCanvas = useRef(null);

  // Al abrir, sugerir el peso estimado (kg) según las prendas; el usuario confirma o cambia
  useEffect(() => {
    if (isOpen) {
      const estimadoG = (prendas || []).reduce((s, p) => s + ((p.peso || 0) * (p.cantidad || 0)), 0);
      setPesoReal((estimadoG / 1000).toFixed(2));
      setErrors({});
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const totalPrendas = prendas.reduce((sum, p) => sum + p.cantidad, 0);
  const pesoTotal = prendas.reduce((sum, p) => sum + (p.peso * p.cantidad), 0);
  const pesoEstimadoKg = (pesoTotal / 1000).toFixed(2);

  const limpiarError = (campo) => {
    setErrors(prev => {
      if (!prev[campo]) return prev;
      const next = { ...prev };
      delete next[campo];
      return next;
    });
  };

  const handleClearSignature = () => {
    sigCanvas.current?.clear();
    setFirma(null);
  };

  const handleSaveSignature = () => {
    if (sigCanvas.current) {
      const signatureData = sigCanvas.current.toDataURL();
      setFirma(signatureData);
      limpiarError('firma');
    }
  };

  const usarPesoSugerido = () => {
    setPesoReal(pesoEstimadoKg);
    limpiarError('pesoReal');
  };

  // Valida los campos obligatorios y devuelve el objeto de errores
  const validate = () => {
    const nuevosErrores = {};
    if (!receptorNombre.trim()) {
      nuevosErrores.receptorNombre = 'Ingrese el nombre de quien entrega';
    }
    const pesoRealNum = parseFloat(pesoReal);
    if (!pesoReal || isNaN(pesoRealNum) || pesoRealNum <= 0) {
      nuevosErrores.pesoReal = 'Confirme el peso real total (mayor a 0)';
    }
    if (!firma) {
      nuevosErrores.firma = 'La firma de quien entrega es obligatoria';
    }
    setErrors(nuevosErrores);
    return nuevosErrores;
  };

  const handleConfirm = async () => {
    const nuevosErrores = validate();
    if (Object.keys(nuevosErrores).length > 0) {
      return;
    }

    try {
      setIsProcessing(true);

      const pesoRealGramos = Math.round(parseFloat(pesoReal) * 1000);

      // Conectar la impresora Bluetooth dentro del gesto del usuario
      // (requestDevice requiere activación).
      const printer = getPrinterInstance();
      let impresoraLista = printer.isConnected();
      if (!impresoraLista) {
        try {
          await printer.connect();
          impresoraLista = true;
        } catch (connError) {
          console.error('No se pudo conectar la impresora Bluetooth:', connError);
        }
      }

      const creado = await onConfirm({
        receptor_nombre: receptorNombre,
        observaciones,
        firma,
        peso_real: pesoRealGramos,
      });

      // Imprimir solo si la impresora quedó conectada. Si no, avisamos y NO
      // reintentamos (evita un segundo diálogo que deja el modal colgado).
      if (impresoraLista) {
        try {
          await printer.printRecoleccionDobleCopia({
            consecutivo: creado?.consecutivo_recoleccion || creado?.consecutivo,
            fecha: new Date().toISOString(),
            edificio,
            hospedaje,
            receptor_nombre: receptorNombre,
            conductor: creado?.conductor,
            items: prendas,
            peso_real: pesoRealGramos,
          });
        } catch (printError) {
          console.error('Error al imprimir el ticket de recolección:', printError);
          toast.error('Recolección registrada, pero no se pudo imprimir el ticket');
        }
      } else {
        toast.info('Recolección registrada. No se pudo conectar la impresora; puedes reimprimir el comprobante luego.');
      }
    } catch (error) {
      console.error('Error en confirmación:', error);
      toast.error('No se pudo completar la recolección');
    } finally {
      // Siempre liberamos el estado de carga (el padre además cierra el modal)
      setIsProcessing(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b p-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold">Resumen de Recolección</h2>
            <p className="text-sm text-gray-500">Verifique los datos antes de confirmar</p>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Información del Lugar */}
          <Card>
            <CardContent className="p-4">
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Building2 className="h-5 w-5 text-blue-600" />
                  <div>
                    <div className="text-sm text-gray-500">Edificio</div>
                    <div className="font-semibold">{edificio?.nombre}</div>
                  </div>
                </div>
                {hospedaje && (
                  <div className="flex items-center gap-2">
                    <Home className="h-5 w-5 text-green-600" />
                    <div>
                      <div className="text-sm text-gray-500">Hospedaje</div>
                      <div className="font-semibold">{hospedaje.nombrePropiedad}</div>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Resumen de Prendas */}
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold flex items-center gap-2">
                  <Shirt className="h-5 w-5 text-purple-600" />
                  Prendas Recolectadas
                </h3>
                <Badge variant="secondary">
                  <Package className="h-3 w-3 mr-1" />
                  {totalPrendas} prendas
                </Badge>
              </div>
              
              <div className="space-y-2 max-h-[200px] overflow-y-auto">
                {prendas.map((prenda, index) => (
                  <div 
                    key={index}
                    className="flex items-center justify-between p-2 bg-gray-50 rounded"
                  >
                    <div className="flex-1">
                      <div className="font-medium">{prenda.nombre}</div>
                      <div className="text-sm text-gray-500">
                        {prenda.peso}g por unidad
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-bold text-lg">{prenda.cantidad}</div>
                      <div className="text-xs text-gray-500">
                        {(prenda.peso * prenda.cantidad)}g
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-4 pt-4 border-t flex justify-between items-center">
                <span className="font-semibold">Total</span>
                <div className="text-right">
                  <div className="font-bold text-xl">{totalPrendas} prendas</div>
                  <div className="text-sm text-gray-500">{pesoTotal}g total</div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Datos del Receptor */}
          <Card>
            <CardContent className="p-4 space-y-4">
              <div>
                <Label htmlFor="receptor">Nombre de quien entrega *</Label>
                <Input
                  id="receptor"
                  value={receptorNombre}
                  onChange={(e) => {
                    setReceptorNombre(e.target.value);
                    if (e.target.value.trim()) limpiarError('receptorNombre');
                  }}
                  placeholder="Nombre completo"
                  className={`mt-1 ${errors.receptorNombre ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
                />
                {errors.receptorNombre && (
                  <p className="text-xs text-red-600 mt-1">{errors.receptorNombre}</p>
                )}
              </div>

              {/* Peso real total */}
              <div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="pesoReal">Peso real total (kg) *</Label>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs text-blue-600"
                    onClick={usarPesoSugerido}
                    disabled={isProcessing}
                  >
                    Usar sugerido ({pesoEstimadoKg} kg)
                  </Button>
                </div>
                <div className="relative mt-1">
                  <Input
                    id="pesoReal"
                    type="number"
                    min="0"
                    step="0.01"
                    inputMode="decimal"
                    value={pesoReal}
                    onChange={(e) => {
                      setPesoReal(e.target.value);
                      if (parseFloat(e.target.value) > 0) limpiarError('pesoReal');
                    }}
                    placeholder="0.00"
                    className={`pr-10 ${errors.pesoReal ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-400">
                    kg
                  </span>
                </div>
                {errors.pesoReal ? (
                  <p className="text-xs text-red-600 mt-1">{errors.pesoReal}</p>
                ) : (
                  <p className="text-xs text-gray-500 mt-1">
                    Peso estimado por prendas: {pesoEstimadoKg} kg. Confirme o ajuste con el peso real de la báscula.
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="observaciones">Observaciones</Label>
                <textarea
                  id="observaciones"
                  value={observaciones}
                  onChange={(e) => setObservaciones(e.target.value)}
                  placeholder="Observaciones adicionales (opcional)"
                  className="w-full mt-1 p-2 border rounded-md resize-none"
                  rows={3}
                />
              </div>
            </CardContent>
          </Card>

          {/* Firma Digital */}
          <Card>
            <CardContent className="p-4">
              <Label className="mb-2 block">Firma de quien entrega *</Label>
              <div className={`border-2 border-dashed rounded-lg p-2 bg-gray-50 ${
                errors.firma ? 'border-red-500' : 'border-gray-300'
              }`}>
                <SignatureCanvas
                  ref={sigCanvas}
                  canvasProps={{
                    className: 'w-full h-40 bg-white rounded cursor-crosshair',
                  }}
                  onEnd={handleSaveSignature}
                />
              </div>
              {errors.firma && (
                <p className="text-xs text-red-600 mt-1">{errors.firma}</p>
              )}
              <div className="flex gap-2 mt-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleClearSignature}
                  className="flex-1"
                >
                  Limpiar Firma
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white border-t p-4 flex gap-2">
          <Button
            variant="outline"
            className="flex-1"
            onClick={onClose}
          >
            <X className="h-4 w-4 mr-2" />
            Cancelar
          </Button>
          <Button
            className="flex-1"
            onClick={handleConfirm}
            disabled={isProcessing}
          >
            {isProcessing ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Procesando...
              </>
            ) : (
              <>
                <Check className="h-4 w-4 mr-2" />
                Confirmar Recolección
              </>
            )}
          </Button>
        </div>
      </motion.div>
    </div>
  );
};

export default ModalResumenRecoleccion;
